import { useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import HelloprenupLogoWithBlueBackground from "../../icons/HelloprenupLogoWithBlueBackground";
import HelloprenupLogoWithText from "../../icons/HelloprenupLogoWithText";
import { PageLoading } from "../../Loading";
import ErrorMessage, { PageErrorContainer } from "../../Error";
import AttorneyNavBar from "../utils/desktop/NavBar";
import NavItemsContainer from "../utils/desktop/NavItemsContainer";
import NavItem from "../utils/desktop/NavItem";
import { attorneyNavItems, bottomAttorneyNavItems } from "../utils/attorneyNav";
import { COLOR_APP_TEXT } from "../../theme";
import { ParagraphHeaderSmall } from "../../typography/Paragraph";
import Divider from "../../Divider";
import Signout from "../../icons/Signout";
import { SecondaryButtonSmall } from "../../buttons/SecondaryButton";
import WarningPopup from "../../popups/Warning";
import Footer from "../Footer";
import { AttorneyGetPayload } from "../../../../../api/src/types";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import {
  StyledCircleIconButton,
  SignoutAndFooter,
  Spacer,
  NavLogo,
  IconContainer,
  HelloPrenupLogoWithTextContainer,
  FooterContainer,
  Link,
} from "../utils/desktop/components.tsx";
import { AttorneyLinkContainer } from "./components";
import { useLocation } from "react-router";

const AttorneyNav = ({
  attorney = undefined,
  isLoading = false,
  error = false,
}: {
  attorney?: AttorneyGetPayload | undefined;
  isLoading?: boolean;
  error?: boolean | FetchBaseQueryError | SerializedError;
} = {}) => {
  const { signOut } = useClerk();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  if (isLoading) {
    return (
      <AttorneyNavBar
        isExpanded={isExpanded}
        onMouseEnter={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <PageLoading />
      </AttorneyNavBar>
    );
  }

  if (error) {
    return (
      <AttorneyNavBar
        isExpanded={isExpanded}
        onMouseEnter={() => {
          setIsExpanded(true);
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <PageErrorContainer>
          <ErrorMessage>Error occurred</ErrorMessage>
        </PageErrorContainer>
      </AttorneyNavBar>
    );
  }

  const handleLogout = () => {
    setIsExpanded(false);
    setShowLogoutPopup(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutPopup(false);
    dispatch({ type: "USER_LOGOUT" });
    await signOut({ redirectUrl: "/login?attorney=true" });
  };

  const handleCancelLogout = () => {
    setShowLogoutPopup(false);
  };

  return (
    <>
      {showLogoutPopup && (
        <WarningPopup
          title="Are you sure you want to log out?"
          buttons={{
            confirmText: "Log out",
            cancelText: "Cancel",
            onConfirm: handleConfirmLogout,
            onCancel: handleCancelLogout,
          }}
        />
      )}
      <AttorneyNavBar
        role="navigation"
        isExpanded={isExpanded}
        onMouseOver={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <NavLogo isExpanded={isExpanded}>
          {isExpanded ? (
            <HelloPrenupLogoWithTextContainer>
              <HelloprenupLogoWithText />
            </HelloPrenupLogoWithTextContainer>
          ) : (
            <HelloprenupLogoWithBlueBackground />
          )}
        </NavLogo>
        <NavItemsContainer>
          {attorneyNavItems.map((item) => (
            <AttorneyLinkContainer
              key={item.label}
              isDisabled={
                !attorney?.stage || !item.stage.includes(attorney.stage)
              }
            >
              <Link to={item.path} key={item.label}>
                <NavItem
                  key={item.label}
                  isActive={location.pathname === item.path}
                  isExpanded={isExpanded}
                >
                  <IconContainer>{item.icon}</IconContainer>
                  {isExpanded && (
                    <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                      {item.label}
                    </ParagraphHeaderSmall>
                  )}
                </NavItem>
              </Link>
            </AttorneyLinkContainer>
          ))}
          <Spacer />
        </NavItemsContainer>
        <SignoutAndFooter>
          <Divider />
          {bottomAttorneyNavItems.map((item) => (
            <AttorneyLinkContainer
              key={item.label}
              isDisabled={
                !attorney?.stage || !item.stage.includes(attorney.stage)
              }
            >
              <Link to={item.path} key={item.label}>
                <NavItem
                  key={item.label}
                  isActive={location.pathname === item.path}
                  isExpanded={isExpanded}
                >
                  <IconContainer>{item.icon}</IconContainer>
                  {isExpanded && (
                    <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                      {item.label}
                    </ParagraphHeaderSmall>
                  )}
                </NavItem>
              </Link>
            </AttorneyLinkContainer>
          ))}
          {isExpanded ? (
            <SecondaryButtonSmall onClick={handleLogout}>
              Sign&nbsp;out
            </SecondaryButtonSmall>
          ) : (
            <StyledCircleIconButton>
              <Signout />
            </StyledCircleIconButton>
          )}
          {isExpanded && (
            <FooterContainer>
              <Footer />
            </FooterContainer>
          )}
        </SignoutAndFooter>
      </AttorneyNavBar>
    </>
  );
};

export default AttorneyNav;
